import { Button, IconSize, ReloadIcon, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { ResizableDivider } from '../../../components/ResizableDivider';
import { useDataCatalogTree } from '../hooks/useDataCatalogTree';
import { DataCatalogColumnViewer } from './DataCatalogColumnViewer';
import { DataCatalogTree } from './DataCatalogTree';

const MIN_TREE_WIDTH = 240;
const MAX_TREE_WIDTH = 520;
const DEFAULT_TREE_WIDTH = 320;

interface DataCatalogPageProps {
  projectUuid: string;
  breadcrumb?: string;
  canEdit?: boolean;
  refreshKey?: number | string;
}

export function DataCatalogPage({
  projectUuid,
  breadcrumb,
  canEdit,
  refreshKey,
}: DataCatalogPageProps) {
  const { tree, loading, error, reload } = useDataCatalogTree(projectUuid);
  const [selectedCatalog, setSelectedCatalog] = React.useState<string | null>(null);
  const [selectedSchema, setSelectedSchema] = React.useState<string | null>(null);
  const [selectedTable, setSelectedTable] = React.useState<string | null>(null);
  const [treeWidth, setTreeWidth] = React.useState(DEFAULT_TREE_WIDTH);

  function handleSelectTable(catalog: string, schema: string, table: string) {
    setSelectedCatalog(catalog);
    setSelectedSchema(schema);
    setSelectedTable(table);
  }

  function handleResize(delta: number) {
    setTreeWidth((prev) => Math.min(MAX_TREE_WIDTH, Math.max(MIN_TREE_WIDTH, prev + delta)));
  }

  // Clear selection when project changes
  React.useEffect(() => {
    setSelectedCatalog(null);
    setSelectedSchema(null);
    setSelectedTable(null);
  }, [projectUuid]);

  // Re-fetch when settings are saved (refreshKey bumped by parent)
  const isFirstRender = React.useRef(true);
  React.useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    void reload();
  }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps

  // Unconfigured states - no Databricks integration or no catalog configured
  if (
    !loading &&
    (error === 'DATABRICKS_NOT_CONFIGURED' || error === 'DATABRICKS_CATALOG_NOT_CONFIGURED')
  ) {
    const message =
      error === 'DATABRICKS_NOT_CONFIGURED'
        ? 'No Databricks integration is connected to this project. Ask an admin to attach one.'
        : 'No catalog scope has been configured. Ask an admin to configure Data Catalog settings.';

    return (
      <div className="ss-panel ss-panel--fill">
        <header className="ss-panel__header">
          <div className="ss-panel__header-left">
            {breadcrumb && <p className="ss-panel__breadcrumb">{breadcrumb}</p>}
            <h1 className="ss-panel__title">Data Catalog</h1>
          </div>
        </header>
        <section className="ss-browser" aria-label="Data Catalog browser">
          <div className="ss-browser__empty">
            <p>{message}</p>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="ss-panel ss-panel--fill">
      <header className="ss-panel__header">
        <div className="ss-panel__header-left">
          {breadcrumb && <p className="ss-panel__breadcrumb">{breadcrumb}</p>}
          <h1 className="ss-panel__title">Data Catalog</h1>
        </div>
        <div className="ss-panel__header-actions">
          <Tooltip.Simple label="Refresh">
            <Button
              type="button"
              variant="primary"
              size="sm"
              onClick={() => void reload()}
              disabled={loading}
              aria-label="Refresh catalog"
            >
              {loading && (
                <ReloadIcon
                  width={IconSize.sm}
                  height={IconSize.sm}
                  aria-hidden="true"
                  className="ss-panel__action-icon--spinning"
                />
              )}
              {loading ? 'Refreshing…' : 'Refresh'}
            </Button>
          </Tooltip.Simple>
        </div>
      </header>

      <section className="ss-browser" aria-label="Data Catalog browser">
        <div className="ss-browser__layout">
          <aside className="ss-browser__sidebar" style={{ width: `${treeWidth}px` }}>
            <div className="ss-browser__sidebar-body">
              <DataCatalogTree
                tree={tree}
                loading={loading}
                error={error}
                onRetry={() => void reload()}
                selectedCatalog={selectedCatalog}
                selectedSchema={selectedSchema}
                selectedTable={selectedTable}
                onSelectTable={handleSelectTable}
              />
            </div>
          </aside>
          <ResizableDivider
            direction="vertical"
            onResize={handleResize}
            className="ss-browser__divider"
            ariaLabel="Resize catalog tree"
          />
          <div className="ss-browser__viewer-pane">
            <DataCatalogColumnViewer
              projectUuid={projectUuid}
              catalogName={selectedCatalog}
              schemaName={selectedSchema}
              tableName={selectedTable}
              canEdit={canEdit}
            />
          </div>
        </div>
      </section>
    </div>
  );
}
